// timple — 회사 자료, 사업 자료, OKR, 이니셔티브
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../../shared/api/client";
import { Empty, Loading } from "../../shared/components/Empty";
import { Pill } from "../../shared/components/Pill";
import { toast } from "../../shared/stores/toasts";

type Objective = {
  id: number; title: string; description: string | null;
  quarter: string; status: string; progress: number; owner_id: number | null;
  key_results: KeyResult[];
};
type KeyResult = {
  id: number; metric: string; target: number; current: number; unit: string | null; progress_pct: number;
};
type Initiative = {
  id: number; title: string; description: string | null;
  quarter: string; status: string; health: string; owner_id: number | null;
  objective_id: number | null; start_date: string | null; end_date: string | null;
};

const HEALTH_COLORS: Record<string, string> = {
  ON_TRACK: "var(--ok, #22c55e)",
  AT_RISK: "var(--warn, #f59e0b)",
  OFF_TRACK: "#ef4444",
};

const COMPANY_INFO = [
  { id: "general", title: "기업 일반", items: ["상호명: (주)팀플", "대표자: 홍길동", "소재지: 서울특별시 강남구", "주요 사업: IT 솔루션 · ERP 개발"] },
  { id: "cert", title: "인증 및 자격", items: ["ISO 9001:2015 (2025-06 갱신)", "정보보호관리체계(ISMS-P) 취득", "SW 사업자 신고"] },
  { id: "history", title: "연혁", items: ["2020 회사 설립", "2021 Series A 투자 유치", "2022 첫 엔터프라이즈 고객", "2024 Tim-box ERP 출시"] },
  { id: "org", title: "조직 구성", items: ["대표이사실", "경영지원본부 (HR · 재무)", "기술본부 (개발 · QA · 인프라)", "영업본부"] },
];

const BIZ_INFO = [
  { id: "portfolio", title: "포트폴리오", items: ["ERP SaaS — Tim-box (현재)", "웹 에이전시 프로젝트 30+건", "공공기관 SI 5건"] },
  { id: "performance", title: "실적", items: ["2023 ARR ₩2.4억", "2024 ARR ₩6.1억 (YoY +154%)", "고객사 18개"] },
  { id: "tech", title: "보유 기술", items: ["FastAPI · React · TypeScript", "PostgreSQL · Redis · Celery", "Docker · Kubernetes"] },
  { id: "partners", title: "주요 파트너", items: ["Anthropic (AI)", "Google Cloud", "AWS Korea"] },
];

export default function PlanningPage() {
  const [params] = useSearchParams();
  const sub = params.get("filter") || "general";

  const companyItem = COMPANY_INFO.find((i) => i.id === sub);
  const bizItem = BIZ_INFO.find((i) => i.id === sub);

  if (companyItem) return <StaticCard {...companyItem} />;
  if (bizItem) return <StaticCard {...bizItem} />;
  if (sub === "okr") return <OkrView />;
  if (sub === "initiatives") return <InitiativesView />;

  return <StaticCard title="기업 일반" items={COMPANY_INFO[0].items} />;
}

function StaticCard({ title, items }: { title: string; items: string[] }) {
  return (
    <div className="t-grid cols-1">
      <div className="t-card" style={{ maxWidth: 700 }}>
        <h3 style={{ margin: "0 0 12px" }}>{title}</h3>
        <ul style={{ margin: 0, paddingLeft: 20, lineHeight: 2, fontSize: 14 }}>
          {items.map((it, i) => <li key={i}>{it}</li>)}
        </ul>
      </div>
    </div>
  );
}

function OkrView() {
  const [objectives, setObjectives] = useState<Objective[] | null>(null);
  const [quarter, setQuarter] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [newObj, setNewObj] = useState({ title: "", description: "", quarter: "2026-Q2" });

  const load = () =>
    api.get<Objective[]>(`/okr/objectives${quarter ? `?quarter=${quarter}` : ""}`)
      .then((r) => setObjectives(r.data))
      .catch(() => setObjectives([]));

  useEffect(() => { load(); }, [quarter]);

  const add = async () => {
    try {
      await api.post("/okr/objectives", newObj);
      toast.success("목표 추가됨");
      setShowAdd(false);
      setNewObj({ title: "", description: "", quarter: "2026-Q2" });
      load();
    } catch (e: any) {
      toast.error("실패", e?.response?.data?.error?.message);
    }
  };

  const updateKr = async (krId: number, current: number) => {
    try {
      await api.patch(`/okr/key-results/${krId}`, { current });
      load();
    } catch {}
  };

  if (objectives === null) return <Loading />;

  return (
    <div className="t-grid cols-1">
      <div className="t-card" style={{ display: "flex", gap: 12, alignItems: "center" }}>
        <span style={{ fontWeight: 600 }}>목표 · KR</span>
        <select className="t-input" style={{ width: 140, height: 32 }} value={quarter} onChange={(e) => setQuarter(e.target.value)}>
          <option value="">전체 분기</option>
          {["2026-Q1","2026-Q2","2026-Q3","2026-Q4"].map((q) => <option key={q} value={q}>{q}</option>)}
        </select>
        <button className="t-btn solid" style={{ marginLeft: "auto" }} onClick={() => setShowAdd((v) => !v)}>
          {showAdd ? "취소" : "+ 목표 추가"}
        </button>
      </div>

      {showAdd && (
        <div className="t-card" style={{ display: "grid", gap: 10, maxWidth: 500 }}>
          <div><label className="t-label">목표 제목</label><input className="t-input" value={newObj.title} onChange={(e) => setNewObj({ ...newObj, title: e.target.value })}/></div>
          <div><label className="t-label">설명</label><input className="t-input" value={newObj.description} onChange={(e) => setNewObj({ ...newObj, description: e.target.value })}/></div>
          <div>
            <label className="t-label">분기</label>
            <select className="t-input" value={newObj.quarter} onChange={(e) => setNewObj({ ...newObj, quarter: e.target.value })}>
              {["2026-Q1","2026-Q2","2026-Q3","2026-Q4"].map((q) => <option key={q} value={q}>{q}</option>)}
            </select>
          </div>
          <button className="t-btn solid" onClick={add} disabled={!newObj.title}>추가</button>
        </div>
      )}

      {objectives.length === 0 && <Empty msg="등록된 목표가 없습니다" />}

      {objectives.map((obj) => (
        <div key={obj.id} className="t-card">
          <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 10 }}>
            <h3 style={{ margin: 0, flex: 1 }}>{obj.title}</h3>
            <Pill kind="info">{obj.quarter}</Pill>
            <Pill kind={obj.status === "ACTIVE" ? "ok" : "muted"}>{obj.status}</Pill>
          </div>
          {obj.description && <p style={{ fontSize: 13, color: "var(--text-3)", margin: "0 0 10px" }}>{obj.description}</p>}
          <ProgressBar value={obj.progress} />
          {obj.key_results.length > 0 && (
            <div style={{ marginTop: 10, display: "grid", gap: 6 }}>
              {obj.key_results.map((kr) => (
                <div key={kr.id} style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 13 }}>
                  <span style={{ flex: 1, color: "var(--text-2)" }}>{kr.metric}</span>
                  <input type="number" className="t-input" style={{ width: 80, height: 28 }}
                    defaultValue={kr.current}
                    onBlur={(e) => updateKr(kr.id, Number(e.target.value))}/>
                  <span style={{ color: "var(--text-3)", minWidth: 60 }}>/ {kr.target}{kr.unit ? ` ${kr.unit}` : ""}</span>
                  <span style={{ minWidth: 40, textAlign: "right", fontWeight: 600 }}>{kr.progress_pct.toFixed(0)}%</span>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function InitiativesView() {
  const [items, setItems] = useState<Initiative[] | null>(null);
  const [quarter, setQuarter] = useState("");

  const load = () =>
    api.get<Initiative[]>(`/okr/initiatives${quarter ? `?quarter=${quarter}` : ""}`)
      .then((r) => setItems(r.data))
      .catch(() => setItems([]));

  useEffect(() => { load(); }, [quarter]);

  const updateStatus = async (id: number, status: string) => {
    try { await api.patch(`/okr/initiatives/${id}`, { status }); load(); }
    catch {}
  };

  if (items === null) return <Loading />;
  if (items.length === 0) return <Empty msg="등록된 이니셔티브가 없습니다" />;

  return (
    <div className="t-grid cols-1">
      <div className="t-card" style={{ display: "flex", gap: 12, alignItems: "center" }}>
        <span style={{ fontWeight: 600 }}>이니셔티브 ({items.length})</span>
        <select className="t-input" style={{ width: 140, height: 32 }} value={quarter} onChange={(e) => setQuarter(e.target.value)}>
          <option value="">전체 분기</option>
          {["2026-Q1","2026-Q2","2026-Q3","2026-Q4"].map((q) => <option key={q} value={q}>{q}</option>)}
        </select>
      </div>
      <div className="t-grid cols-2">
        {items.map((ini) => (
          <div key={ini.id} className="t-card">
            <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 8 }}>
              <span style={{ width: 10, height: 10, borderRadius: "50%", background: HEALTH_COLORS[ini.health] ?? "var(--text-3)", flexShrink: 0 }}/>
              <h4 style={{ margin: 0, flex: 1, fontSize: 14 }}>{ini.title}</h4>
              <Pill kind="info">{ini.quarter}</Pill>
            </div>
            {ini.description && <p style={{ fontSize: 12, color: "var(--text-3)", margin: "0 0 8px" }}>{ini.description}</p>}
            <div style={{ display: "flex", gap: 6, fontSize: 12 }}>
              <select className="t-input" style={{ height: 28, fontSize: 12 }} value={ini.status}
                onChange={(e) => updateStatus(ini.id, e.target.value)}>
                {["PLANNED","IN_PROGRESS","DONE","CANCELLED"].map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <span style={{ color: HEALTH_COLORS[ini.health], alignSelf: "center", fontWeight: 600 }}>
                {ini.health.replace("_", " ")}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ProgressBar({ value }: { value: number }) {
  const pct = Math.min(100, Math.max(0, value));
  return (
    <div style={{ height: 6, background: "var(--bg-2)", borderRadius: 3, overflow: "hidden" }}>
      <div style={{ height: "100%", width: `${pct}%`, background: "var(--p-500)", borderRadius: 3, transition: "width 0.3s" }}/>
    </div>
  );
}
